// @ts-check
/**
 * @file api/account.js
 * @location src/integrations/supabase/api/account.js
 * @role 계정 삭제·복원 RPC + Edge Function 래퍼.
 *
 *  삭제·복원은 Edge Function 경유(30일 grace 설정/해제). 결제 provider 해지는 토스 이관 시 재구현.
 *  account state 조회는 SupabaseGate 가 hydrate 후 1회 호출 → grace 표시 분기.
 */
import { supabase } from '../client.js';
import { callRpc } from './_call.js';

/**
 * Edge Function 호출 — rpc_delete_account (30일 grace).
 * @returns {Promise<{ scope: 'team' | 'member' | 'unknown' }>}
 */
export async function deleteAccount() {
  const { data, error } = await supabase.functions.invoke('delete-account-and-cancel', { body: {} });
  if (error) throw error;
  return data;
}

/**
 * 30일 grace 안에서 본인/팀 복원. Edge Function 경유 — scope 는 서버가 결정.
 * @returns {Promise<{ scope: 'team' | 'member' | 'no_op' | 'unknown' }>}
 */
export async function restoreAccount() {
  const { data, error } = await supabase.functions.invoke('restore-account-and-resume', { body: {} });
  if (error) throw error;
  return data;
}

/**
 * 현재 비밀번호 재인증 — 개인정보(비밀번호·메일) 변경 전 본인 확인.
 *   signInWithPassword 성공 = 비번 일치(같은 유저라 세션 갱신은 무해).
 *   세션이 살아있는 채 자리를 비운 사이 임의 변경되는 계정 탈취를 막는 표준 재인증.
 * @param {string|undefined} email 현재 로그인 이메일
 * @param {string} password 입력한 현재 비밀번호
 * @returns {Promise<boolean>} true=일치(재인증 통과)
 */
export async function reauthenticate(email, password) {
  if (!email || !password) return false;
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  return !error;
}

/**
 * 본인/팀 grace 상태 조회. SupabaseGate 가 hydrate 후 호출.
 * @returns {Promise<'team' | 'member' | null>}
 */
export async function getAccountState() {
  return callRpc('rpc_account_state', {}, (data) => {
    const row = Array.isArray(data) ? data[0] : data;
    return row?.deleted_scope ?? null;
  });
}
